import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { AlertTriangle, Search, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { client } from '@/lib/api';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const addVendorFormSchema = z.object({
  categoryId: z.string().uuid('Select a category'),
  name: z.string().min(1, 'Vendor name is required').max(255),
  vendorContactName: z.string().max(255).optional(),
  vendorEmail: z.string().email('Invalid email').max(255),
  vendorPhone: z.string().max(50).optional(),
  vendorWebsite: z.string().max(2048).optional().or(z.literal('')),
});

type AddVendorForm = z.infer<typeof addVendorFormSchema>;

const rolodexFormSchema = z.object({
  categoryId: z.string().uuid('Select a category'),
});

type RolodexForm = z.infer<typeof rolodexFormSchema>;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Category {
  id: string;
  name: string;
}

interface VendorSearchResult {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
}

interface VendorContact {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  deactivatedAt: string | null;
}

interface VendorDetail extends VendorSearchResult {
  contacts: VendorContact[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  categories: Category[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AddVendorDialog({ open, onOpenChange, projectId, categories }: Props) {
  const queryClient = useQueryClient();

  // ----- Create-new mode -----
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<AddVendorForm>({
    resolver: zodResolver(addVendorFormSchema),
    defaultValues: {
      categoryId: '',
      name: '',
      vendorContactName: '',
      vendorEmail: '',
      vendorPhone: '',
      vendorWebsite: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: AddVendorForm) => {
      const res = await client.api.items.$post({
        json: {
          categoryId: data.categoryId,
          name: data.name,
          vendorContactName: data.vendorContactName || null,
          vendorEmail: data.vendorEmail,
          vendorPhone: data.vendorPhone || null,
          vendorWebsite: data.vendorWebsite || null,
        },
      });
      if (!res.ok) throw new Error('Failed to create vendor');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast.success('Vendor added!');
      onOpenChange(false);
      reset();
    },
  });

  // ----- Rolodex mode -----
  const {
    handleSubmit: handleRolodexSubmit,
    setValue: setRolodexValue,
    formState: { errors: rolodexErrors },
    reset: resetRolodex,
  } = useForm<RolodexForm>({
    resolver: zodResolver(rolodexFormSchema),
    defaultValues: { categoryId: '' },
  });

  const [rolodexCategoryId, setRolodexCategoryId] = useState('');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, 250);
  const [pickedVendor, setPickedVendor] = useState<VendorSearchResult | null>(null);
  const [rolodexSubmitError, setRolodexSubmitError] = useState<string | null>(null);

  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ['vendors', 'search', debouncedSearch],
    queryFn: async () => {
      const res = await client.api.vendors.$get({
        query: { search: debouncedSearch },
      });
      if (!res.ok) throw new Error('Failed to search vendors');
      return res.json() as Promise<VendorSearchResult[]>;
    },
    enabled: debouncedSearch.trim().length > 0,
    staleTime: 30_000,
  });

  const rolodexMutation = useMutation({
    mutationFn: async ({ categoryId, vendor }: { categoryId: string; vendor: VendorDetail }) => {
      const primaryContact = vendor.contacts.find(
        (c) => c.isPrimary && c.deactivatedAt === null,
      ) ?? null;

      const res = await client.api.items.$post({
        json: {
          categoryId,
          name: vendor.name,
          vendorId: vendor.id,
          vendorContactName: primaryContact?.name ?? null,
          vendorEmail: primaryContact?.email ?? vendor.email ?? null,
          vendorPhone: primaryContact?.phone ?? vendor.phone ?? null,
          vendorWebsite: vendor.website ?? null,
        },
      });
      if (!res.ok) throw new Error('Failed to add vendor');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast.success('Vendor added!');
      onOpenChange(false);
      handleDialogClose();
    },
  });

  // Fetch full vendor detail (with contacts) when user picks one from search results
  const { data: pickedVendorDetail, isFetching: isFetchingDetail } = useQuery({
    queryKey: ['vendor', pickedVendor?.id],
    queryFn: async () => {
      const res = await client.api.vendors[':id'].$get({
        param: { id: pickedVendor!.id },
      });
      if (!res.ok) throw new Error('Failed to fetch vendor details');
      return res.json() as Promise<VendorDetail>;
    },
    enabled: pickedVendor !== null,
    staleTime: 60_000,
  });

  function handleDialogClose() {
    reset();
    resetRolodex();
    setRolodexCategoryId('');
    setSearchText('');
    setPickedVendor(null);
    setRolodexSubmitError(null);
  }

  function onRolodexSubmit(data: RolodexForm) {
    if (!pickedVendorDetail) {
      setRolodexSubmitError('Select a vendor from the list.');
      return;
    }
    setRolodexSubmitError(null);
    rolodexMutation.mutate({ categoryId: data.categoryId, vendor: pickedVendorDetail });
  }

  const primaryContact = pickedVendorDetail?.contacts.find(
    (c) => c.isPrimary && c.deactivatedAt === null,
  ) ?? null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleDialogClose();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Vendor</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="create" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="create" className="flex-1">Create new</TabsTrigger>
            <TabsTrigger value="rolodex" className="flex-1">From rolodex</TabsTrigger>
          </TabsList>

          {/* ----------------------------------------------------------------
              Mode 1 — Create new
          ---------------------------------------------------------------- */}
          <TabsContent value="create">
            <form onSubmit={handleSubmit((data) => createMutation.mutate(data))} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Category *</label>
                <Select onValueChange={(v) => setValue('categoryId', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.categoryId && <p className="text-sm text-destructive">{errors.categoryId.message}</p>}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Vendor Name *</label>
                <Input {...register('name')} placeholder="e.g. Sweet Scoops Gelato Cart" />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Contact Name</label>
                <Input {...register('vendorContactName')} placeholder="e.g. Maria Rodriguez" />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Email *</label>
                <Input {...register('vendorEmail')} type="email" placeholder="vendor@example.com" />
                {errors.vendorEmail && <p className="text-sm text-destructive">{errors.vendorEmail.message}</p>}
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Phone</label>
                <Input {...register('vendorPhone')} type="tel" placeholder="(555) 123-4567" />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium">Website</label>
                <Input {...register('vendorWebsite')} type="text" placeholder="example.com" />
                {errors.vendorWebsite && <p className="text-sm text-destructive">{errors.vendorWebsite.message}</p>}
              </div>

              {createMutation.isError && (
                <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Failed to add vendor. Please try again.
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Adding…' : 'Add Vendor'}
                </Button>
              </div>
            </form>
          </TabsContent>

          {/* ----------------------------------------------------------------
              Mode 2 — From rolodex
          ---------------------------------------------------------------- */}
          <TabsContent value="rolodex">
            <form onSubmit={handleRolodexSubmit(onRolodexSubmit)} className="space-y-4 pt-2">
              {/* Category */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Category *</label>
                <Select
                  value={rolodexCategoryId}
                  onValueChange={(v) => {
                    setRolodexCategoryId(v);
                    setRolodexValue('categoryId', v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rolodexErrors.categoryId && (
                  <p className="text-sm text-destructive">{rolodexErrors.categoryId.message}</p>
                )}
              </div>

              {/* Search */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Search rolodex</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      if (pickedVendor) setPickedVendor(null);
                    }}
                    placeholder="Type to search…"
                    className="pl-8"
                  />
                </div>
              </div>

              {/* Results list */}
              {debouncedSearch.trim().length > 0 && !pickedVendor && (
                <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                  {isSearching ? (
                    <p className="px-3 py-2 text-sm text-muted-foreground">Searching…</p>
                  ) : searchResults && searchResults.length > 0 ? (
                    <ul>
                      {searchResults.map((v) => (
                        <li key={v.id}>
                          <button
                            type="button"
                            onClick={() => setPickedVendor(v)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                          >
                            <span className="font-medium">{v.name}</span>
                            {v.email && (
                              <span className="ml-2 text-muted-foreground">{v.email}</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-3 py-2 text-sm text-muted-foreground">No vendors found.</p>
                  )}
                </div>
              )}

              {/* Selected vendor preview */}
              {pickedVendor && (
                <div className="rounded-md border border-border bg-muted/40 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                        <span className="font-medium text-sm truncate">{pickedVendor.name}</span>
                      </div>
                      {isFetchingDetail ? (
                        <p className="mt-1 text-xs text-muted-foreground">Loading contact…</p>
                      ) : primaryContact ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Primary contact: {primaryContact.name}
                          {primaryContact.email && ` — ${primaryContact.email}`}
                        </p>
                      ) : pickedVendorDetail?.email ? (
                        <p className="mt-1 text-xs text-muted-foreground">{pickedVendorDetail.email}</p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setPickedVendor(null);
                        setSearchText('');
                      }}
                      className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Change
                    </button>
                  </div>
                </div>
              )}

              {/* Form-level error (no vendor selected) */}
              {rolodexSubmitError && (
                <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {rolodexSubmitError}
                </div>
              )}

              {rolodexMutation.isError && (
                <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Failed to add vendor. Please try again.
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={rolodexMutation.isPending || isFetchingDetail}>
                  {rolodexMutation.isPending ? 'Adding…' : 'Add Vendor'}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
